import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { useTranslations } from "next-intl";

import myaccount from "~/components/common/myaccount.module.scss";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import useGiftcard from "~/hooks/use-giftcard";
import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import intlUtils from "~/utils/intl-utils";
import productUtils from "~/utils/product-utils";

import LinkWrapper from "../common/link-wrapper";

export default function PurchasesCard() {
  const t = useTranslations();
  const { user, isAuthenticated } = useAuthContext();

  const listOrders = hooks.useCallService({ service: Core.ListOrdersForCustomer });
  const listOrderItemImages = hooks.useCallService({ service: Core.GetProducts });
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);

  const priceFormatter = intlUtils.usePriceFormatter();
  const breakpoint = useBreakpoint();

  const { data } = useQuery({
    queryKey: ["getLatestOrder", user?.ID],
    staleTime: Infinity,
    queryFn: async () => {
      if (user) {
        const orderList = await listOrders({
          ID: user.ID,
          PageConfig: {
            Limit: 5,
            SortDirection: 1,
            Filter: {
              ExcludeOrderProperties: Constants.RETURN_ORDER,
            },
          },
        });
        if (orderList && orderList.Result.Page.length > 0) {
          const mostRecentOrder = orderList.Result.Page.find((order) => {
            const filteredLines = order.Lines.filter(
              (line) => !!line.ProductID && ((line.IsCancelled && line.FulfillmentStatus === 800) || !line.IsCancelled)
            );
            return (
              filteredLines.length > 0 &&
              (order.Payments.length > 0 || filteredLines.some((line) => line.ShippingMethod?.PaymentHandledByCarrier))
            );
          });
          if (mostRecentOrder) {
            const actualProductLines = mostRecentOrder.Lines.filter(
              (el) => !!el.ProductID && ((el.IsCancelled && el.FulfillmentStatus === 800) || !el.IsCancelled)
            );
            const prodIds = actualProductLines.map((el) => el.ProductID!);
            const hasDigitalGiftCard = actualProductLines.some((el) => !el.IsCancelled && el.IsDigitalGiftCard);

            const images = await listOrderItemImages({
              ProductIDs: [...prodIds],
              IncludedFields: ["primary_image", "display_value"],
            });

            let composedImagesList = actualProductLines.map((el) => ({
              ID: el.ID,
              product_id: el.ProductID,
            })) as CrossCountry.ProductWithImageOnly[];

            if (images) {
              let imageListByProductId = Object.values(images.Products);
              composedImagesList = composedImagesList.map((el) => {
                const currentImage = imageListByProductId.find((image) => image.product_id == el.product_id);
                return { ...el, ...currentImage };
              });
            }
            return {
              actualCount: actualProductLines.length,
              order: mostRecentOrder,
              images: composedImagesList,
              hasDigitalGiftCard: hasDigitalGiftCard,
            };
          } else {
            return null;
          }
        }
        return null;
      }
    },
    enabled: !!(evaEndpointUrl && isAuthenticated),
  });

  const digitalGiftCardsQuery = useGiftcard().prepareGetDigitalGiftcardOptionsQuery(data?.order);

  if (!data) {
    return <></>;
  }

  const order = data.order;
  const images = data.images?.slice(0, 5);

  return (
    <div className={myaccount.myaccountCard}>
      <div className={myaccount.myaccountCardHeader}>
        <h2>{t("generic.myaccount.dashboard.lastOrderTitle")}</h2>
        <LinkWrapper href="purchases">{t("generic.myaccount.dashboard.viewAllPurchasesLink")}</LinkWrapper>
      </div>
      <div className={myaccount.myaccountCardContent}>
        {order ? (
          <div className={myaccount.lastOrder}>
            <div className={myaccount.lastOrderHeader}>
              <p>
                {t("generic.myaccount.dashboard.lastOrderSubtitle", { status: t(productUtils.getOrderStatus(order)) })}
                {}
              </p>
              {breakpoint == "desktop" && (
                <LinkWrapper href={`purchases/${order.ID}`}>
                  {t("generic.myaccount.dashboard.viewOrderDetailsLink")}
                </LinkWrapper>
              )}
            </div>
            <div className={myaccount.lastOrderContent}>
              <div className={myaccount.lastOrderContentData}>
                {t.rich("generic.myaccount.dashboard.lastOrderDetails", {
                  status: (chunks) => (
                    <p>
                      {chunks} <span>{t(productUtils.getOrderStatus(order))}</span>
                    </p>
                  ),
                  number: (chunks) => (
                    <p>
                      {chunks} <span>{order?.ID}</span>
                    </p>
                  ),
                  price: (chunks) => (
                    <p>
                      {order.TotalItems || data.actualCount} {chunks}
                      {priceFormatter.currency(order.TotalAmountInTax, { currency: order?.CurrencyID })}
                    </p>
                  ),
                })}
              </div>
              <div className={myaccount.images}>
                {images?.map((el, i) => {
                  const digitalBg = digitalGiftCardsQuery.data?.find((card) => card.Identifiers.OrderLineID === el.ID)
                    ?.Data?.Theme?.ImageUrl;
                  return (
                    <Image
                      key={i}
                      src={digitalBg || el.primary_image?.url || imageUtils.transparentPixel}
                      alt={el.display_value || ""}
                      width={breakpoint == "desktop" ? 80 : 64}
                      height={breakpoint == "desktop" ? 80 : 64}
                    />
                  );
                })}
              </div>
            </div>
            {breakpoint == "mobile" && (
              <div className={myaccount.linkList}>
                {/* <LinkWrapper href="#">Track order</LinkWrapper> */}
                <LinkWrapper href={`purchases/${order.ID}`}>
                  {t("generic.myaccount.dashboard.viewOrderDetailsLink")}
                </LinkWrapper>
              </div>
            )}
          </div>
        ) : (
          <div className={myaccount.emptyPurchases}>
            {t.rich("generic.myaccount.dashboard.emptyPurchases", {
              p: (chunks) => <p>{chunks}</p>,
              link: (chunks) => (
                <LinkWrapper href="#" lookAsButton="secondary">
                  {chunks}
                </LinkWrapper>
              ),
            })}
          </div>
        )}
      </div>
    </div>
  );
}
